import { AlertType, TableCell, TableHeader } from '@toggle/design-system';
import { getCurrencySymbol } from '@toggle/helpers/src/utils/currency/currency';
import { TFunction, useTranslation } from 'next-i18next';
import React from 'react';

import { AssetDetails } from '@/api/asset/asset-schema';
import {
  checkEarningEstimate,
  formatNumberWithUnit,
} from '@/utils/asset/assetUtils';

import {
  CellValueType,
  ExpectationCell,
} from '../expectation-cell/ExpectationCell';
import { TooltipContainer } from '../tooltip-container/TooltipContainer';
import * as S from './EarningsTable.styles';

interface HeadingData {
  name: string;
  tooltip?: string;
}

export const headingData = (
  t: TFunction,
  currency: string,
  symbol: string
): HeadingData[] => {
  return [
    {
      name: t('currency', { currency: currency.toUpperCase() }),
    },
    {
      name: t('expected'),
      tooltip: t('expectedTooltip'),
    },
    {
      name: t('reported'),
      tooltip: t('reportedTooltip'),
    },
    {
      name: t('surpriseCurrency', { currency: symbol }),
      tooltip: t('surpriseCurrencyTooltip'),
    },
    {
      name: t('surprisePercent'),
      tooltip: t('surprisePercentTooltip'),
    },
  ];
};

type EarningTableRows = 'eps' | 'revenue';

interface EarningTableProps {
  assetDetails: AssetDetails;
}

const tableRows: EarningTableRows[] = ['eps', 'revenue'];

export const EarningsTable = ({ assetDetails }: EarningTableProps) => {
  const { t } = useTranslation('common');
  const currencySymbol = getCurrencySymbol(assetDetails.currency);
  const headingsData = headingData(t, assetDetails.currency, currencySymbol);
  const showAlert = !checkEarningEstimate(assetDetails.eps);

  return (
    <S.Container>
      {showAlert && (
        <S.StyledAlert
          data-testid="no-estimation-alert"
          title={t('common:table.alertTitle', {
            fiscalPeriod: assetDetails.fiscal_period,
          })}
          type={AlertType.Info}
          variant="inline"
        />
      )}
      <S.Title>
        {t('common:table.title', {
          fiscalPeriod: assetDetails.fiscal_period,
          fiscalYear: assetDetails.fiscal_year,
        })}
      </S.Title>
      <S.StyledTable
        withBodyRowHover={false}
        TableHeaderContent={
          <tr>
            {headingsData.map((d, i) => (
              <TableHeader
                key={i}
                isHoverEnabled={!!d.tooltip}
                alignment={i === 0 ? 'start' : 'end'}
              >
                {d.tooltip ? (
                  <S.TableHeaderTooltipText>
                    <TooltipContainer label={d.tooltip} inPortal>
                      <span>{d.name}</span>
                    </TooltipContainer>
                  </S.TableHeaderTooltipText>
                ) : (
                  <span>{d.name}</span>
                )}
              </TableHeader>
            ))}
          </tr>
        }
        TableBodyContent={tableRows.map((d, i) => (
          <tr key={i}>
            <TableHeader alignment="start" isHoverEnabled={false}>
              {t(d)}
            </TableHeader>
            <TableCell suppressHydrationWarning>
              {formatNumberWithUnit(assetDetails[d].estimate)}
            </TableCell>
            <TableCell suppressHydrationWarning>
              {formatNumberWithUnit(assetDetails[d].value)}
            </TableCell>
            <ExpectationCell
              value={
                assetDetails[d].surprise === undefined
                  ? '-'
                  : (assetDetails[d].surprise as number)
              }
            />
            <ExpectationCell
              value={
                assetDetails[d].surprise_percentage === undefined
                  ? '-'
                  : (assetDetails[d].surprise_percentage as number)
              }
              typeOfValue={CellValueType.Percentage}
            />
          </tr>
        ))}
      />
    </S.Container>
  );
};
